import { Box, Grid, Divider, Stack, Typography, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { LoadingButton } from '@mui/lab';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Iconify, NextImage } from '@sdf/base';
import { useGoogleLoginHandler } from '../../hooks';
import { getOryFrontend } from '../../ory/client';
import { SxProps, useMediaQuery } from '@mui/system';
import { loginStyle as style } from '../../styles';
import { Styles } from '@t2c/shared-fe';
import { LOGIN_SCREEN_VIDEO, UPLOADED_PUBLIC_ASSETS_URL } from '../../utils/constants';
import PasswordAuth from './PasswordAuth';

interface Props {
  onSuccess: () => void;
  onError: () => void;
  isLoading: boolean;
  isLoggedIn: boolean;
  handleRedirect: () => void;
  redirectBtnText: string;
}

const bottomMenue = [
  {
    title: 'Pricing',
    path: '/pricing',
  },
  {
    title: 'Privacy',
    path: 'https://txt2create.com/privacy-policy',
  },
  {
    title: 'Terms Of Use',
    path: 'https://txt2create.com/terms-of-use',
  },
];

const imageUrls = [
  '/assets/login-page/1.webp',
  '/assets/login-page/2.webp',
  '/assets/login-page/3.webp',
  '/assets/login-page/4.webp',
];

// SvgShape Component
interface SvgShapeProps {
  width: number;
  height: number;
  rotation?: 'normal' | 'reverse';
  position?: { bottom?: number; left?: number; right?: number; top?: number };
  sx?: SxProps;
}

const SvgShape: React.FC<SvgShapeProps> = ({
  width,
  height,
  rotation = 'normal',
  position,
  sx,
}) => {
  const transform = rotation === 'reverse' ? 'scaleX(-1)' : '';
  return (
    <Box
      sx={{
        position: 'absolute',
        width: `${width}px`,
        height: `${height}px`,
        transform,
        bottom: position?.bottom ?? 'auto',
        left: position?.left ?? 'auto',
        right: position?.right ?? 'auto',
        top: position?.top ?? 'auto',
        ...sx,
      }}
    >
      <svg
        width={width}
        height={height}
        viewBox="0 0 162 547"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M177 547V-27L0 260L177 547Z" fill={`url(#paint0_linear_9735_50627)`} />
        <defs>
          <linearGradient
            id={'paint0_linear_9735_50627'}
            x1="-419.111"
            y1="365.254"
            x2="161.829"
            y2="365.254"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.2" stopColor="#7B35EF" />
            <stop offset="1" stopColor="#7B35EF" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </Box>
  );
};

// Login Component
export const Login: React.FC<Props> = ({
  onSuccess,
  onError,
  isLoading,
  isLoggedIn,
  handleRedirect,
  redirectBtnText,
}) => {
  const { handleLogin, isLoginButtonDisabled } = useGoogleLoginHandler({ onSuccess, onError });
  const { push } = useRouter();
  const theme = useTheme();
  const [linkHint, setLinkHint] = useState<string | null>(null);

  // Detect popup/flow context to render a minimal loader-only UI and auto-start OIDC
  const [isPopupFlow] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const isPopupParam = params.get('popup') === '1';
    const flow = params.get('flow');
    const mode = params.get('mode');
    // Consider it a popup if window opened by another page OR explicit popup OR on the OIDC login flow callback
    return !!window.opener || isPopupParam || (!!flow && mode === 'login');
  });

  // In popup context, update the title for a nicer UX
  useEffect(() => {
    if (!isPopupFlow) return;
    const prev = document.title;
    document.title = 'Signing in…';
    return () => {
      document.title = prev;
    };
  }, [isPopupFlow]);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isBlurred, setIsBlurred] = useState(false);

  const isExtraSmall = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    const interval = setInterval(() => {
      setIsBlurred(true);
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
        setIsBlurred(false);
      }, 500);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // If popup sign-in determined account linking is required, surface a clear hint.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const linkReq = params.get('link_required');
      const flagged = window.sessionStorage.getItem('auth_link_required');
      if (linkReq === '1' || flagged) {
        setLinkHint(
          'An account already exists for this email. Sign in with your email and password, then go to Profile → Link with Google to enable Google login.'
        );
        // Clear the one-shot flag so the message does not persist across sessions
        if (flagged) window.sessionStorage.removeItem('auth_link_required');

        // If the account is actually linked now, hide the hint and clean up the URL param.
        (async () => {
          try {
            const ory = getOryFrontend();
            const { data } = await ory.createBrowserSettingsFlow();
            const nodes = (data as any)?.ui?.nodes || [];
            const unlinkGoogleNode = nodes.find(
              (n: any) =>
                n?.group === 'oidc' &&
                String(n?.attributes?.name || '').toLowerCase() === 'unlink' &&
                /google/i.test(n?.attributes?.value || '')
            );
            if (unlinkGoogleNode) {
              setLinkHint(null);
              // remove link_required from URL so it doesn't reappear
              const newParams = new URLSearchParams(window.location.search);
              newParams.delete('link_required');
              const newUrl = `${window.location.pathname}${
                newParams.size ? `?${newParams.toString()}` : ''
              }`;
              window.history.replaceState({}, '', newUrl);
            }
          } catch {
            // ignore (no session / not linked)
          }
        })();
      }
    } catch {}
  }, []);

  // If this page is loaded as the popup flow page (?flow=...&mode=login), auto-start Google OIDC once.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const flow = params.get('flow');
      const mode = params.get('mode');
      if (flow && mode === 'login') {
        const key = `oidc_started_${flow}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          handleLogin();
        }
      }
    } catch (e) {
      // ignore
    }
  }, [handleLogin]);

  // If this is a freshly opened popup (no flow params yet), auto-start OIDC once as well
  useEffect(() => {
    if (!isPopupFlow) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const hasFlow = params.has('flow');
      const hasMode = params.get('mode') === 'login';
      if (!hasFlow) {
        const key = `oidc_started_popup`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          handleLogin();
        }
      } else if (hasFlow && hasMode) {
        // Already handled by the other effect
      }
    } catch {}
  }, [isPopupFlow, handleLogin]);

  // In popup context, show a minimal loader UI to avoid flashing intermediate components
  if (isPopupFlow) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: (t) => t.palette.background.default,
          p: 3,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <NextImage src="/assets/logoWithText.svg" alt="Logo" width={180} height={36} />
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Redirecting to Google…
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Grid container sx={{ height: '100vh' }}>
      <Grid
        item
        xs={12}
        lg={7}
        display={{ xs: 'none', lg: 'block' }}
        sx={{ overflow: 'hidden', borderTopRightRadius: 30, borderBottomRightRadius: 30 }}
      >
        <video
          src={UPLOADED_PUBLIC_ASSETS_URL + LOGIN_SCREEN_VIDEO}
          height={'100%'}
          width={'100%'}
          style={{ objectFit: 'cover' }}
          autoPlay
          loop
          muted
        />
      </Grid>

      <Grid
        item
        xs={12}
        lg={5}
        sx={{
          height: '100%',
          position: 'relative',
          background: 'none',
          backgroundSize: 'cover',
          backgroundPositionX: '50% !important',
          backgroundPositionY: '-80px !important',
          overflow: 'hidden',
          backgroundRepeat: 'no-repeat !important',
        }}
      >
        {/* <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'linear-gradient(to top, rgba(0, 0, 0, 1) 40%, rgba(0, 0, 0, 0) 100%)',
            zIndex: 0,
          }}
        /> */}

        <SvgShape
          width={162}
          height={547}
          rotation="normal"
          position={{ right: 0 }}
          sx={{
            opacity: 1,
          }}
        />
        <SvgShape
          width={162}
          height={547}
          rotation="reverse"
          position={{}}
          sx={{
            opacity: { xs: 0, sm: 1 },
            bottom: { xs: -40, sm: -240, lg: -40 },
            left: -45,
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: { xs: '100%', sm: '75%' },
            marginX: 'auto',
          }}
        >
          <Stack sx={style.loginContainer} spacing={2} alignItems="center">
            <Stack
              sx={{
                position: { xs: 'absolute', lg: 'static' },
                top: { xs: '0px', sm: '-50px', md: '0' },
                left: { xs: '16px', sm: '16px', lg: '0' },
                ml: { xs: '16px', sm: 0 },
              }}
            >
              <NextImage
                src="/assets/logoWithText.svg"
                alt="Logo"
                width={264}
                height={51}
                style={{ borderRadius: 0 }}
                sx={{ width: { xs: 130, sm: 150, md: 264 } }}
              />
            </Stack>

            <Stack pt={2}>
              <Typography
                fontWeight={600}
                mt={2}
                sx={{ fontSize: { xs: 50, lg: 24 }, lineHeight: { xs: '54px', lg: '33px' } }}
              >
                Welcome {isExtraSmall ? <br /> : ''} to TXT2create
              </Typography>

              <Stack mt={{ xs: 2, sm: 8 }}>
                {isLoggedIn ? (
                  <LoadingButton variant="outlined" onClick={handleRedirect} sx={style.loginBtn}>
                    {redirectBtnText}
                  </LoadingButton>
                ) : (
                  <>
                    <Typography
                      fontWeight={300}
                      lineHeight={'26.88px'}
                      sx={{
                        color: (theme) => theme.palette.background.activeBorder,
                        maxWidth: { xs: '100%', lg: 375 },
                        textAlign: 'left',
                        fontSize: { xs: 17, sm: 20 },
                      }}
                    >
                      Click on the{' '}
                      <span onClick={isLoginButtonDisabled ? handleLogin : () => null}>below</span>{' '}
                      button to sign in using google account
                    </Typography>
                    {linkHint && (
                      <Typography
                        sx={{
                          mt: 1,
                          color: (theme) => theme.palette.error.light,
                          maxWidth: { xs: '100%', lg: 420 },
                          textAlign: 'left',
                          fontSize: { xs: 14, sm: 15 },
                        }}
                      >
                        {linkHint}
                      </Typography>
                    )}
                    <LoadingButton
                      variant="contained"
                      startIcon={
                        <Iconify icon={'logos:google-icon'} sx={style.googleIcon as SxProps} />
                      }
                      sx={style.loginBtn}
                      loading={isLoading}
                      onClick={handleLogin}
                      disabled={isLoginButtonDisabled}
                    >
                      Login with Google
                    </LoadingButton>
                    {/* Email / Password tabs and forms */}
                    <Box sx={{ width: '100%', mt: 2 }}>
                      <PasswordAuth
                        onAuthSuccess={onSuccess}
                        isLoginButtonDisabled={isLoginButtonDisabled}
                      />
                    </Box>
                  </>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    mt: 6,
                    alignItems: 'center',
                    position: { xs: 'absolute', sm: 'relative' },
                    bottom: 0,
                  }}
                >
                  {bottomMenue.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{
                          pr: 2,
                          pl: index > 0 ? 2 : 0,
                          cursor: 'pointer',
                          color: 'white',
                          textAlign: 'left',
                          fontSize: 13,
                          '&:hover': {
                            color: (theme) => theme.palette.background.activeBorder,
                          },
                        }}
                        onClick={() => push(item.path)}
                      >
                        {item.title}
                      </Typography>
                      {index !== bottomMenue.length - 1 && (
                        <Divider orientation="vertical" flexItem sx={{ bgcolor: 'gray' }} />
                      )}
                    </Box>
                  ))}
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Grid>
    </Grid>
  );
};

const styles: Styles = {};
